import {
  BoundSelectField,
  BoundSelectFieldProps,
  Css,
  Icon,
  IconButton,
  Palette,
  SelectField,
  Tag,
  useComputed,
} from "@homebound/beam";
import { FieldState } from "@homebound/form-state";
import { Maybe } from "graphql/jsutils/Maybe";
import { useState } from "react";
import {
  LocationPathField_FeatureTypeFragment,
  LocationPathField_LocationFragment,
  useLocationPathField_GetFeatureTypesQuery,
  useLocationPathField_GetLocationsQuery,
} from "src/components/locationPathField/LocationPathField.generated";
import { FeatureTypesFilter, LocationsFilter, LocationType } from "src/generated/graphqlTypes";

type LocationPathFieldProps = Omit<
  BoundSelectFieldProps<LocationPathField_LocationFragment, string>,
  "options" | "multiselect" | "getOptionLabel" | "getOptionValue"
> & {
  /**
   * Additional filtering for the locations query (e.g., `{ rpav: [rpavId] }`)
   * merged with the component's defaults of `{ scope: false, version: [2] }`
   */
  filter?: LocationsFilter;
  /**
   * The fully-assembled location path (e.g., g:Building~l:Level 1~r:Kitchen~f:Island~f:Base)
   * when a new feature is added under the selected location
   */
  newLocationPathField: FieldState<Maybe<string>>;
};

/**
 * Combines a Location (Group/Level/Room/Feature) dropdown with an optional feature type picker for adding a new feature
 * under the selected location. When a feature type is selected, the picker writes the fully-assembled location path
 * (i.e., parent path joined with the selected feature type) to `newLocationPathField`.
 */
export function LocationPathField(props: LocationPathFieldProps) {
  const {
    placeholder = "Select a Location",
    onSelect,
    newLocationPathField,
    field,
    filter,
    disabled,
    ...otherProps
  } = props;

  const { data, loading } = useLocationPathField_GetLocationsQuery({
    variables: { filter: { scope: false, version: [2], ...filter } },
  });

  // TODO: Update locationFilter to include type, sort by path
  // SC-55529 - https://app.shortcut.com/homebound-team/story/55529/chore-be-update-locationsfilter-to-support-type-and-sortby-path
  // @ts-ignore path is never undefined on v2 locations
  const locations = (data?.locations ?? []).sortByKey("path");

  const selectedLocation = useComputed(() => {
    return locations.find((l) => l.id === field.value);
  }, [field, locations]);
  const [showAddLocationField, setShowAddLocationField] = useState(false);

  const addFeatureDisabledReason =
    // A caller-passed `disabled={false}` falls through to our internal logic.
    disabled ||
    (!selectedLocation
      ? "Select a location first"
      : selectedLocation.type.code === LocationType.Feature && !selectedLocation.featureType
        ? "Can't add a location under a non-standard feature"
        : false);

  return (
    <>
      <div css={Css.df.aic.gap2.$}>
        <BoundSelectField
          {...otherProps}
          field={field}
          label="Location"
          disabled={disabled}
          onSelect={(locationId, location) => {
            // BoundSelectField's field.set is replaced when passing our own onSelect, so write to the field manually
            field.set(locationId);
            // Clear any pending feature pick when the parent changes
            newLocationPathField.set(undefined);
            setShowAddLocationField(false);
            onSelect?.(locationId, location);
          }}
          placeholder={loading ? "Loading..." : placeholder}
          options={locations}
          getOptionValue={(o) => o.id}
          getOptionLabel={(o) => o.name}
          getOptionMenuLabel={({ name, displayLocationPath, type, featureType }) => (
            <span css={Css.df.aic.gap1.$}>
              <Tag xss={Css.m0.$} type="info" text={type.name} />
              <span>{name}</span>
              {/* Flag non-standard features with a warning icon */}
              {type.code === LocationType.Feature && !featureType && (
                <Icon icon="error" color={Palette.Yellow600} inc={2} tooltip="Non-standard feature" xss={Css.fs0.$} />
              )}
              {name !== displayLocationPath && <span css={Css.gray500.$}>{displayLocationPath}</span>}
            </span>
          )}
          autoSort={false}
        />
        {!showAddLocationField && (
          // Add top padding to center
          <div css={Css.ptPx(26).$}>
            <IconButton
              color={addFeatureDisabledReason ? Palette.Gray400 : Palette.Blue500}
              data-testid="addSubLocationBtn"
              tooltip="Add new feature location"
              disabled={addFeatureDisabledReason}
              onClick={() => setShowAddLocationField(true)}
              icon="plus"
            />
          </div>
        )}
      </div>
      {showAddLocationField && selectedLocation && (
        <FeatureTypePicker
          parent={selectedLocation}
          newLocationPathField={newLocationPathField}
          onClose={() => {
            newLocationPathField.set(undefined);
            setShowAddLocationField(false);
          }}
        />
      )}
    </>
  );
}

type FeatureTypePickerProps = {
  parent: LocationPathField_LocationFragment;
  newLocationPathField: FieldState<Maybe<string>>;
  onClose: () => void;
};

/**
 * Picks a feature type at any depth under the parent location (e.g., "Island", "Island > Base", etc.).
 * The selected type is appended to the parent's path with `~f:` separators and written to `newLocationPathField`.
 */
export function FeatureTypePicker(props: FeatureTypePickerProps) {
  const { parent, newLocationPathField, onClose } = props;
  // Filter based on the location that the feature chain lives under
  const filter: FeatureTypesFilter = parent.room?.roomType
    ? { roomTypes: [parent.room.roomType.id] } // A specific room (e.g., "Kitchen")
    : parent.level
      ? { allowedInLevel: true } // A level (e.g., "Level 1")
      : parent.group
        ? { allowedInBuilding: true } // Building
        : {}; // Features must at least exist under Building, so this should never happen
  const { data, loading } = useLocationPathField_GetFeatureTypesQuery({ variables: { filter } });
  // Strip the parent path back off so the SelectField's value matches an option (which is keyed by the chain alone).
  const value = useComputed(() => {
    const path = newLocationPathField.value;
    const prefix = `${parent.path}~f:`;
    return path?.startsWith(prefix) ? path.slice(prefix.length) : undefined;
  }, [newLocationPathField, parent.path]);
  const options = buildFeatureTypeOptions({ featureTypes: data?.featureTypes ?? [], parent });
  return (
    <div css={Css.df.aic.gap2.$}>
      <SelectField
        label={`Feature of ${parent.name}`}
        data-testid="newLocationPath"
        value={value}
        onSelect={(chain) => newLocationPathField.set(chain ? `${parent.path}~f:${chain}` : undefined)}
        options={options}
        getOptionValue={(chain) => chain.join("~f:")}
        getOptionLabel={(chain) => chain.join(" > ")}
        getOptionMenuLabel={(chain) => {
          const ancestors = chain.slice(0, -1);
          return (
            <span css={Css.df.aic.gap1.$}>
              {ancestors.nonEmpty && <span css={Css.gray500.$}>{`${ancestors.join(" > ")} >`}</span>}
              <span>{chain.last}</span>
            </span>
          );
        }}
        placeholder={
          loading ? "Loading..." : options.isEmpty ? `No features available under ${parent.name}` : "Select a feature"
        }
        autoSort={false}
      />
      <div css={Css.ptPx(26).$}>
        <IconButton color={Palette.Blue500} tooltip="Remove child location" onClick={onClose} icon="x" />
      </div>
    </div>
  );
}

/**
 * Flattens the feature type tree into every possible path from the parent's feature type down
 * (e.g., ["Island"], ["Island", "Base"], ["Island", "Base", "Drawer"]). Each path becomes a SelectField option.
 */
function buildFeatureTypeOptions(params: {
  featureTypes: LocationPathField_FeatureTypeFragment[];
  parent: LocationPathField_LocationFragment;
}): string[][] {
  const { featureTypes, parent } = params;
  const childrenByParentId = featureTypes.groupBy((f) => f.parent?.id ?? "");
  const rootId = parent.featureType?.id ?? "";
  // Recursively build paths from the parent's feature type down
  const out: string[][] = [];
  function walk(prefix: string[], node: LocationPathField_FeatureTypeFragment) {
    const path = [...prefix, node.name];
    out.push(path);
    for (const child of childrenByParentId[node.id] ?? []) walk(path, child);
  }
  for (const start of (childrenByParentId[rootId] ?? []).sortByKey("name")) {
    walk([], start);
  }
  return out;
}
